import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router";
import { Body, H1 } from "@datapay-by-datacom/design-system";
import { CompanyContextStep } from "../../onboarding";
import type { CompanyContext } from "../../onboarding";
import { PayComponentImportDropzone } from "../components/PayComponentImportDropzone";
import { WizardProvider } from "../components/WizardProvider";
import { WizardStepper } from "../components/WizardStepper/WizardStepper";
import { useActiveWizardStepIndex } from "../hooks/useActiveWizardStepIndex";
import { usePayComponentImportEnabled } from "../hooks/usePayComponentImportEnabled";
import { usePayComponentImportRedesignEnabled } from "../hooks/usePayComponentImportRedesignEnabled";
import { PAY_COMPONENT_IMPORT_DISPLAY_NAME } from "../config";
import {
  clearWizardSession,
  loadBoundCompanyFromSession,
} from "../utils/wizardSession";
import styles from "./PayComponentImportLayout.module.css";

/**
 * Root layout for the Pay Component Import wizard.
 *
 * Nested under `/onboarding`, so the page shell (`<WorkspaceScreen wide>`) is
 * provided by the parent `OnboardingLayout` — this layout only supplies the
 * wizard chrome, mirroring `BulkCreateCompanyLayout`.
 *
 * Responsibilities:
 * - Gates access on `enable-pay-component-import` (plus parent `enable-onboarding`).
 * - Gates the wizard behind `CompanyContextStep` (the shared company +
 *   environment selection gate). The gate is shown on step 1 (Upload) and
 *   hidden from step 2 onwards so users cannot switch companies mid-import.
 * - Renders the `WizardStepper` progress indicator above the wizard screens.
 *
 * Access is flag-only — no `AgenticProvisioner` role check.
 *
 * Note: the gate validates a *real* company code against the live lookup
 * endpoint, but import/commit still run fixture-backed until the Christmas
 * mocks (#932/#892) are flipped off — so a code that validates here may still
 * import against fixtures for now. That live round-trip is a deferred item.
 */
export function PayComponentImportLayout() {
  const { isLoaded, areFlagsOn } = usePayComponentImportEnabled();
  const redesignEnabled = usePayComponentImportRedesignEnabled();
  // Redesign: no session resume on refresh (Work Patterns parity). Legacy still
  // restores company + wizard from sessionStorage (#228).
  const [boundContext, setBoundContext] = useState<CompanyContext | null>(() => {
    if (redesignEnabled) {
      clearWizardSession();
      return null;
    }
    return loadBoundCompanyFromSession();
  });
  const [companyContextLocked, setCompanyContextLocked] = useState(false);
  const redesignClearedRef = useRef(redesignEnabled);
  const activeStepIndex = useActiveWizardStepIndex();

  // If redesign flips on after first paint (LD default → delivered true), clear
  // any legacy-restored company/session so refresh parity still holds.
  useEffect(() => {
    if (!redesignEnabled || redesignClearedRef.current) return;
    redesignClearedRef.current = true;
    clearWizardSession();
    setBoundContext(null);
  }, [redesignEnabled]);

  const showWizard = boundContext !== null;
  // Show the gate on step 0 so the user can see (and re-validate) the company
  // they're importing for; hide it once they advance past Upload.
  const showCompanyGate = !showWizard || activeStepIndex === 0;
  const showVisiblePageHeader = !showWizard || activeStepIndex === 0;
  const showVisuallyHiddenPageTitle = showWizard && activeStepIndex > 0;

  if (!isLoaded) {
    return null;
  }

  if (!areFlagsOn) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className={styles.shell}>
      {showVisiblePageHeader && (
        <header className={styles.pageHeader}>
          <H1>{PAY_COMPONENT_IMPORT_DISPLAY_NAME}</H1>
          <Body className={styles.pageSubtitle}>
            Upload a CSV file to bulk-assign pay components with values to employees. Rows
            are validated before committing — you can review mappings and any issues
            before confirming.
          </Body>
        </header>
      )}
      {showVisuallyHiddenPageTitle && (
        <h1 className={styles.visuallyHidden}>{PAY_COMPONENT_IMPORT_DISPLAY_NAME}</h1>
      )}

      {showCompanyGate && (
        <CompanyContextStep
          disabled={redesignEnabled && companyContextLocked}
          onContextBound={setBoundContext}
          onContextCleared={() => setBoundContext(null)}
        />
      )}

      {!showWizard && (
        <PayComponentImportDropzone isDisabled={true} />
      )}

      {showWizard && (
        <div className={styles.wizard}>
          {!redesignEnabled && <WizardStepper />}
          <WizardProvider
            companyCode={boundContext.companyCode}
            companyName={boundContext.companyName}
            initialMypayEnvironment={boundContext.mypayEnvironment}
            onBusyChange={
              redesignEnabled ? setCompanyContextLocked : undefined
            }
          />
        </div>
      )}
    </div>
  );
}
